import React from "react";
import locale from "locale";
import { ListGroup, ListGroupItem, Button } from "react-bootstrap";

import { FIELDS_OPTIONS, MODEL_ADAPTER, PARTICIPATION_TYPE_HYBRID } from "./constants";
import EventCalendarModal from "./components/EventCalendarModal";
import { useNotificationContext } from "../../UI/Notifications";
import HtmlPreview from "../../UI/HtmlPreview";
import Breadcrumbs from "../../UI/Breadcrumbs";
import {
    formDataBuilder,
    getEventCalendarSettings,
    updateEventCalendarSettings,
    previewEventCalendarSettings,
} from "../../../../api/cp";

const activeCrumbs = ["mainMenu.emails", "mainMenu.emails.calendar"];

const EventCalendarSettings = ({ event }) => {
    const { showNotification } = useNotificationContext();
    const [calendarSettings, setCalendarSettings] = React.useState(null);
    const [calendarSettingsByType, setCalendarSettingsByType] =
        React.useState(null);
    const [editableSettings, setEditableSettings] = React.useState(null);
    const [previewTemplate, setPreviewTemplate] = React.useState(null);
    const [editableOptions, setEditableOptions] = React.useState(null);

    React.useEffect(() => {
        fetchCalendarSettings();
        setEditableOptions(
            event.participationType === PARTICIPATION_TYPE_HYBRID
                ? FIELDS_OPTIONS
                : FIELDS_OPTIONS.filter(({callbackKey}) => callbackKey === event.participationType)
        );
    }, []);

    async function fetchCalendarSettings() {
        try {
            const response = await getEventCalendarSettings(
                event.eventPassword
            );
            setCalendarSettings(response);
            handleSplitSettings(response);
        } catch ({ message = null }) {
            message && showNotification({ message });
        }
    }

    function handleSplitSettings(settings) {
        const splittedByType = Object.entries(settings).reduce(
            (acc, [key, value]) => {
                if (key.toLowerCase().includes("online")) {
                    acc.online[MODEL_ADAPTER.online[key]] = value;
                } else if (key.toLowerCase().includes("offline")) {
                    acc.offline[MODEL_ADAPTER.offline[key]] = value;
                } else {
                    acc.online[key] = value;
                    acc.offline[key] = value;
                }

                return acc;
            },
            {
                online: {
                    type: "online",
                },
                offline: {
                    type: "offline",
                },
            }
        );

        setCalendarSettingsByType(splittedByType);
    }

    function onChangeCalendarSettings({ propName, propValue }) {
        setEditableSettings((prev) => ({
            ...prev,
            [propName]: propValue,
        }));
    }

    async function onPreviewCalendar() {
        try {
            const isSuccess = await onSaveCalendarSettings(false);

            if (!isSuccess) {
                return showNotification({
                    message: locale("email.eventCalendarSaveError"),
                });
            }

            const response = await previewEventCalendarSettings(
                event.eventPassword,
                editableSettings.type
            );

            setPreviewTemplate(response);
        } catch ({ message = null }) {
            message && showNotification({ message });
        }
    }

    async function onSaveCalendarSettings(updatable) {
        try {
            const payload = mergeCalendarSettings();
            const response = await updateEventCalendarSettings(
                event.eventPassword,
                formDataBuilder(payload)
            );

            if (!updatable) {
                return true;
            }

            handleSplitSettings(response);
            setEditableSettings(null);

            return true;
        } catch ({ message = null }) {
            message && showNotification({ message });

            return false;
        }
    }

    function mergeCalendarSettings() {
        const adaptedSettings = Object.entries(
            MODEL_ADAPTER[editableSettings.type]
        ).reduce((acc, [key, value]) => {
            acc[key] = editableSettings[value];

            return acc;
        }, {});

        return Object.assign({}, calendarSettings, adaptedSettings);
    }

    return (
        <section className="event-calendar-settings">
            <Breadcrumbs
                link={`/event/${event.eventPassword}`}
                activeCrumbs={activeCrumbs}
            />
            <ListGroup>
                {editableOptions && (
                    editableOptions.map((option) => (
                        <ListGroupItem key={option.label} style={option.styles}>
                            {option.label}
                            <Button
                                onClick={() =>
                                    setEditableSettings(
                                        calendarSettingsByType[option.callbackKey]
                                    )
                                }
                            >
                                {locale("email.edit")}
                            </Button>
                        </ListGroupItem>
                    ))
                )}
            </ListGroup>
            {editableSettings && (
                <EventCalendarModal
                    calendarSettings={editableSettings}
                    onChange={onChangeCalendarSettings}
                    onPreview={onPreviewCalendar}
                    onSave={() => onSaveCalendarSettings(true)}
                    onClose={() => setEditableSettings(null)}
                />
            )}
            {/* <HtmlPreview
                isModalVisible={Boolean(previewTemplate)}
                template={previewTemplate}
                onHide={() => setPreviewTemplate(null)}
            /> */}
        </section>
    );
};

export default EventCalendarSettings;
